import {
  computeKinship,
  type GraphMember,
  type KinshipResult,
} from '@/contexts/FamilyGraphContext';

/**
 * Canonical Tree generation authority.
 *
 * Positive tiers are above the viewer, negative tiers are below the viewer,
 * and tier zero contains the viewer's generation.
 *
 * Stored relationship labels are never treated as generation truth here.
 */
export const generationTierFromKinship = (
  kinship: KinshipResult,
): number | null => {
  switch (kinship.code) {
    case 'self':
    case 'spouse':
    case 'sibling':
      return 0;

    case 'parent':
      return 1;

    case 'child':
      return -1;

    case 'ancestor':
      return Math.max(2, kinship.generationsUp);

    case 'descendant':
      return -Math.max(2, kinship.generationsDown);

    case 'pibling':
      return Math.max(1, kinship.generationsUp);

    case 'nibling':
      return -Math.max(1, kinship.generationsDown);

    case 'cousin':
      return kinship.generationsUp - kinship.generationsDown;

    case 'extended':
    case 'unknown':
    default:
      return null;
  }
};

export const computeStructuralGenerationTier = (
  members: GraphMember[],
  viewerId: string,
  targetId: string,
): number | null =>
  generationTierFromKinship(
    computeKinship(members, viewerId, targetId),
  );

export const computeReachedGenerationTiers = (
  members: GraphMember[],
  viewerId: string | null | undefined,
): Set<number> => {
  const tiers = new Set<number>([0]);

  if (!viewerId || !members.some((member) => member.id === viewerId)) {
    return tiers;
  }

  for (const member of members) {
    if (member.approvalStatus !== 'approved') continue;
    if (member.id === viewerId) continue;

    const tier = computeStructuralGenerationTier(
      members,
      viewerId,
      member.id,
    );

    if (tier !== null) tiers.add(tier);
  }

  return tiers;
};
